import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Book, Database, Users, Mail, Lock, Server, 
  BarChart2, Settings, Search, ChevronDown, 
  ChevronRight, ExternalLink, Webhook, Code, Key,
  Copy, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert } from "@/components/feedback/Alert";
import { PageHeader } from "@/components/layout/page/PageHeader";
import { PageContainer } from "@/components/layout/containers/PageContainer";

type Section = {
  id: string;
  title: string;
  icon: React.ElementType;
  content: {
    id: string;
    title: string;
    description: string;
    codeExample?: string;
    response?: {
      success: any;
      error: any;
    };
    subsections?: {
      title: string;
      content: string;
    }[];
  }[];
};

export function DocumentationPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('introducao');
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);

  const sections: Section[] = [
    {
      id: 'introducao',
      title: 'Introdução',
      icon: Book,
      content: [
        {
          id: 'visao-geral',
          title: 'Visão Geral',
          description: 'O Painel Administrativo é uma aplicação web desenvolvida para gerenciar e monitorar o engajamento dos alunos nos cursos da plataforma.',
          subsections: [
            {
              title: 'Principais Funcionalidades',
              content: `
                • Gestão completa de alunos
                • Monitoramento de engajamento
                • Análise de métricas em tempo real
                • Relatórios detalhados
                • Integração com múltiplas bases de dados
                • Webhooks para eventos em tempo real
                • API RESTful com autenticação por token
              `
            },
            {
              title: 'Tecnologias Utilizadas',
              content: `
                • React com TypeScript
                • Tailwind CSS para estilização
                • Supabase para banco de dados
                • Firebase para autenticação
                • Vite como bundler
                • Sistema de Webhooks
                • API RESTful
              `
            }
          ]
        }
      ]
    },
    {
      id: 'autenticacao',
      title: 'Autenticação',
      icon: Lock,
      content: [
        {
          id: 'auth-token',
          title: 'Autenticação por Token',
          description: 'Todas as requisições à API devem incluir um token JWT no header Authorization.',
          codeExample: `
fetch('https://api.exemplo.com/alunos', {
  headers: {
    'Authorization': 'Bearer seu-token-jwt',
    'Content-Type': 'application/json'
  }
})`,
          response: {
            success: {
              token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
              expires_in: 3600
            },
            error: {
              message: "Token inválido ou expirado",
              code: "AUTH_ERROR"
            }
          }
        }
      ]
    },
    {
      id: 'endpoints',
      title: 'Endpoints',
      icon: Server,
      content: [
        {
          id: 'alunos',
          title: 'Alunos',
          description: 'Endpoints para gerenciamento de alunos.',
          codeExample: `
// Listar alunos
GET /api/alunos

// Buscar aluno específico
GET /api/alunos/{id}

// Atualizar aluno
PUT /api/alunos/{id}`,
          response: {
            success: {
              id: "uuid",
              nome: "string",
              email: "string",
              cursos: ["string"]
            },
            error: {
              message: "Aluno não encontrado",
              code: "NOT_FOUND"
            }
          }
        }
      ]
    }
  ];

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSnippet(id);
    setTimeout(() => setCopiedSnippet(null), 2000);
  };

  const filteredSections = sections.filter(section =>
    section.content.some(item =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subsections?.some(sub =>
        sub.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.content.toLowerCase().includes(searchTerm.toLowerCase())
      )
    )
  );

  const activeContent = sections.find(s => s.id === activeSection)?.content || [];

  return (
    <PageContainer>
      <div className="flex">
        {/* Menu Lateral */}
        <div className="w-64 h-screen bg-card border-r border-border p-4 fixed left-0 top-0">
          <div className="flex items-center gap-2 mb-6">
            <Book className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Documentação</h2>
          </div>

          <div className="mb-4">
            <Input
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>

          <nav className="space-y-1">
            {filteredSections.map(section => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;

              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm
                    transition-colors
                    ${isActive 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-muted-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span>{section.title}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Conteúdo */}
        <div className="ml-64 flex-1 p-8">
          <div className="max-w-4xl mx-auto">
            {/* Header com botão de voltar */}
            <PageHeader
              title={sections.find(s => s.id === activeSection)?.title || ''}
              icon={<Book className="w-6 h-6 text-primary" />}
              showBackButton
              onBack={() => window.history.back()}
              actions={
                <a
                  href="https://documenter.getpostman.com/view/neurosaber-api"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary hover:text-primary/90 transition-colors"
                >
                  Ver no Postman
                  <ExternalLink className="w-4 h-4" />
                </a>
              }
            />

            {/* Alerta de Ambiente */}
            <Alert
              variant="info"
              title="Ambiente de Desenvolvimento"
              message="Esta documentação se refere ao ambiente de desenvolvimento. Para produção, utilize o domínio api.neurosaber.com.br"
              className="mb-8"
            />

            {/* Conteúdo da Seção */}
            <div className="space-y-8">
              {activeContent.map(item => (
                <Card key={item.id}>
                  <CardHeader>
                    <CardTitle>{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-6">{item.description}</p>

                    {item.codeExample && (
                      <div className="relative bg-card rounded-lg p-4 mb-6 font-mono text-sm">
                        <pre className="overflow-x-auto">{item.codeExample}</pre>
                        <button
                          onClick={() => copyToClipboard(item.codeExample, item.id)}
                          className="absolute top-2 right-2 p-2 hover:bg-primary/10 rounded-lg transition-colors"
                        >
                          {copiedSnippet === item.id ? (
                            <Check className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <Copy className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    )}

                    {item.response && (
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div>
                          <p className="text-sm text-emerald-400 mb-2">Sucesso (200)</p>
                          <div className="bg-card rounded-lg p-4">
                            <pre className="text-sm overflow-x-auto">
                              {JSON.stringify(item.response.success, null, 2)}
                            </pre>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-red-400 mb-2">Erro (4xx/5xx)</p>
                          <div className="bg-card rounded-lg p-4">
                            <pre className="text-sm overflow-x-auto">
                              {JSON.stringify(item.response.error, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}

                    {item.subsections && (
                      <div className="space-y-4">
                        {item.subsections.map((subsection, index) => (
                          <Card key={index}>
                            <CardHeader>
                              <CardTitle className="text-lg">
                                {subsection.title}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="text-muted-foreground whitespace-pre-line">
                                {subsection.content}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}